import React, { useEffect, useState } from 'react';
import _, { flowRight, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { get } from 'session';
import PropTypes from 'prop-types';
import Select from 'react-select';
import NumberFormat from 'react-number-format';
import useFormValidator from 'useFormValidator';
import Decimal from 'decimal.js';
import { MAX_LIMIT } from 'modules/common/constants';
import * as actions from '../../../actions';

const Activities = ({
  details,
  businessLineId,
  getNecessaryFieldsList,
  isRequested,
  onChange,
  statusId,
}) => {
  const [fieldList, setFieldList] = useState([]);
  const filteredNecessaryFieldsList = fieldList.filter(item => !item.quantity_matrix);
  const filteredQuantityMatrix = fieldList.filter(
    item => _.isArray(item.quantity_matrix) && item.quantity_matrix.length > 0
  );

  const hasGrossAmount =
    details &&
    !_.isEmpty(details) &&
    Object.keys(details).some((key) => key.includes('gross_amount_'));

  const requiredFields =
    details && !_.isEmpty(details)
      ? Object.keys(details).reduce((acc, key) => {
          acc[key] = 'required';
          return acc;
        }, {})
      : {};
  const activitiesValidator = useFormValidator(details, requiredFields);

  useEffect(() => {
    activitiesValidator.validate();
  }, [JSON.stringify(details)]);

  useEffect(() => {
    const applicationStatusId = get('applicationStatusId');

    if (!applicationStatusId || !businessLineId) return;

    const payload = {
      business_line_id: businessLineId,
      application_status_id: statusId || applicationStatusId || 0,
    };
    getNecessaryFieldsList(payload, ({ data }) => setFieldList(data));
  }, [businessLineId]);

  const formatFieldLabel = (key) => {
    if (typeof key !== 'string') return '';

    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const selectOptions = (list) => {
    const newList = [];

    if (_.isEmpty(list)) return false;

    list.map((data) => {
      return newList.push({
        label: formatFieldLabel(_.get(data, 'multiplier'))
          .replace(/_/g, ' ')
          .replace(/^(.)|\s+(.)/g, (x) => x.toUpperCase()),
        value: data.multiplier,
      });
    });
    return newList;
  };

  if (isEmpty(filteredNecessaryFieldsList)) return <></>;

  return (
    <>
      {!hasGrossAmount && (
        <div className="col-12 form-group">
          <label htmlFor="gross_amount" className="form-label mb-0">
            Gross Amount{' '}
            {isRequested && activitiesValidator.renderMessage('gross_amount')}
          </label>
          <NumberFormat
            name="gross_amount"
            id="gross_amount"
            placeholder="0.00"
            className="form-control"
            onValueChange={(values) => {
              const { value } = values;
              onChange(
                'multiplier',
                'gross_amount',
                value,
              );
            }}
            displayType="input"
            decimalScale={2}
            isNumericString
            allowNegative
            thousandSeparator
            allowLeadingZeros
            prefix="₱ "
            disabled={!isRequested}
            value={_.get(details, 'gross_amount', 0)}
            isAllowed={(values) => {
              const { value } = values;
              if (!value) return true;
              const val = new Decimal(value);
              const max = new Decimal(MAX_LIMIT);
              return value === undefined || val.lte(max);
            }}
          />
        </div>
      )}

      {filteredNecessaryFieldsList.map((field) => {
        const multiplier = _.get(field, 'multiplier');
        const options = _.get(field, 'options');

        if (options) {
          return (
            <div className="col-12 form-group" key={multiplier}>
              <label htmlFor={multiplier} className="form-label mb-0">
                {formatFieldLabel(multiplier)}{' '}
                {isRequested && activitiesValidator.renderMessage(multiplier)}
              </label>
              <Select
                id={multiplier}
                name={multiplier}
                options={selectOptions(options)}
                value={selectOptions(options).find(
                  (option) =>
                    _.get(details, multiplier) === _.get(option, 'value')
                )}
                onChange={(e) => onChange('multiplier', multiplier, e.value)}
                components={{ IndicatorSeparator: () => null }}
                isDisabled={!isRequested}
              />
            </div>
          );
        }

        return (
          <div className="col-12 form-group" key={multiplier}>
            <label htmlFor={multiplier} className="form-label mb-0">
              {formatFieldLabel(multiplier)}{' '}
              {isRequested && activitiesValidator.renderMessage(multiplier)}
            </label>
            <input
              id={multiplier}
              className="form-control"
              type="number"
              name={multiplier}
              onChange={(e) => onChange('multiplier', multiplier, e.target.value)}
              value={details[multiplier]}
              disabled={!isRequested}
            />
          </div>
        );
      })}
      {!_.isEmpty(filteredQuantityMatrix) ? (
        filteredQuantityMatrix.map((item) => {
          const multiplier = _.get(item, 'multiplier', '');

          return (
            <React.Fragment>
              <div className='form-group col-12 mb-0'>
                <label className='form-label mb-0 text-capitalize'>
                  {multiplier}
                </label>
              </div>
              <div className='w-100 border rounded-lg mx-3'>
                <table className="table table-borderless table-sm table-striped table-hover mb-0">
                  <thead>
                    <tr className='form-group'>
                      <td className='col-4 form-label text-center border-right'>Unit</td>
                      <td className='col-4 form-label text-center border-right'>Capacity</td>
                      <td className='col-4 form-label text-center'>Number of Units</td>
                    </tr>
                  </thead>
                  <tbody>
                    {item.quantity_matrix.map(quantityMatrixItem => {
                      const quantityMatrixMultiplier = _.get(quantityMatrixItem, 'multiplier');

                      return (
                        <tr key={quantityMatrixMultiplier} className="border-top form-group">
                          <td className="col-4 text-left form-label border-right">
                            {_.get(quantityMatrixItem, 'unit')} 
                          </td>
                          <td className="col-4 text-left form-label border-right">
                            {_.get(quantityMatrixItem, 'capacity')}
                          </td>
                          <td className="col-4 text-left form-label">
                            <NumberFormat
                              name={quantityMatrixMultiplier}
                              id={quantityMatrixMultiplier}
                              allowLeadingZeros
                              className="form-control"
                              onValueChange={(values) => {
                                const { floatValue } = values;
                                onChange(
                                  'quantity_matrix',
                                  quantityMatrixMultiplier,
                                  floatValue
                                );
                              }}
                              displayType="input"
                              thousandSeparator
                              isNumericString
                              value={_.get(details?.quantity_matrix, quantityMatrixMultiplier)}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </React.Fragment>
          )
        })
        
      ) : ''}
    </>
  );
};

Activities.defaultProps = {
  isRequested: false,
  onChange: () => {},
  statusId: 0,
};

// PropTypes for type checking
Activities.propTypes = {
  details: PropTypes.instanceOf(Object).isRequired,
  businessLineId: PropTypes.number.isRequired,
  getNecessaryFieldsList: PropTypes.instanceOf(Function).isRequired,
  isRequested: PropTypes.bool,
  onChange: PropTypes.instanceOf(Function),
  statusId: PropTypes.number,
};

const mapStateToProps = () => ({});

const enhance = flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(Activities);
